import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  X,
  Calendar,
  User,
  Flag,
  Link2,
  Clock,
  AlignLeft,
  Loader2,
  ChevronDown,
  Plus,
  AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';

interface CreateGanttItemModalProps {
  projectId: string;
  onClose: () => void;
  onCreated?: (item: any) => void;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
}

const PRIORITIES = [
  { value: 0, label: 'No priority', color: 'bg-gray-200' },
  { value: 1, label: 'Low', color: 'bg-green-500' },
  { value: 2, label: 'Medium', color: 'bg-yellow-500' },
  { value: 3, label: 'High', color: 'bg-orange-500' },
  { value: 4, label: 'Urgent', color: 'bg-red-500' },
];

export function CreateGanttItemModal({
  projectId,
  onClose,
  onCreated,
  defaultStartDate,
  defaultEndDate,
}: CreateGanttItemModalProps) {
  const api = useApi();
  const queryClient = useQueryClient();

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState(
    defaultStartDate?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
  );
  const [dueDate, setDueDate] = useState(
    defaultEndDate?.toISOString().split('T')[0] || ''
  );
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [stateId, setStateId] = useState<string>('');
  const [priority, setPriority] = useState(2); // Default to medium
  const [estimate, setEstimate] = useState<string>('');
  const [parentId, setParentId] = useState<string>('');
  const [blockedByIds, setBlockedByIds] = useState<string[]>([]);
  const [showBlockerSelect, setShowBlockerSelect] = useState(false);

  // Fetch states/statuses
  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId),
    enabled: !!projectId,
  });

  // Fetch members for assignee
  const { data: members } = useQuery({
    queryKey: ['members', projectId],
    queryFn: () => api.getMembers(projectId),
  });

  // Fetch existing work items for parent/blocker selection
  const { data: workItems } = useQuery({
    queryKey: ['workitems', projectId],
    queryFn: () => api.getWorkItems(projectId),
    enabled: !!projectId,
  });

  // Set default state when states load
  useEffect(() => {
    if (states?.length && !stateId) {
      // Find "To Do" or first non-backlog state, or just first state
      const todoState = states.find((s: any) =>
        s.bh_name.toLowerCase() === 'todo' ||
        s.bh_name.toLowerCase() === 'to do'
      );
      const firstActiveState = states.find((s: any) => s.bh_group >= 2);
      setStateId(todoState?.bh_stateid || firstActiveState?.bh_stateid || states[0]?.bh_stateid || '');
    }
  }, [states, stateId]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      const item = await api.createWorkItem({
        bh_name: title.trim(),
        bh_description: description.trim() || null,
        bh_project: projectId,
        bh_state: stateId,
        bh_priority: priority,
        bh_assignee: assigneeId || null,
        bh_parent: parentId || null,
        bh_estimate: estimate ? parseFloat(estimate) : null,
        bh_startdate: startDate || null,
        bh_targetdate: dueDate || null,
      });

      // Create blocker relations if any
      if (blockedByIds.length > 0) {
        for (const blockerId of blockedByIds) {
          try {
            await api.createRelation({
              bh_issue: item.bh_workitemid,
              bh_related_issue: blockerId,
              bh_relation_type: 'blocked_by',
              bh_project: projectId,
            });
          } catch (e) {
            console.warn('Failed to create blocker relation:', e);
          }
        }
      }

      return item;
    },
    onSuccess: (item) => {
      queryClient.invalidateQueries({ queryKey: ['workitems', projectId] });
      onCreated?.(item);
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !stateId) return;
    createMutation.mutate();
  };

  const calculateDuration = () => {
    if (!startDate || !dueDate) return null;
    const start = new Date(startDate);
    const end = new Date(dueDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays + 1 : null;
  };

  const duration = calculateDuration();

  const addBlocker = (id: string) => {
    if (!blockedByIds.includes(id)) {
      setBlockedByIds([...blockedByIds, id]);
    }
    setShowBlockerSelect(false);
  };

  const removeBlocker = (id: string) => {
    setBlockedByIds(blockedByIds.filter(b => b !== id));
  };

  const getItemName = (id: string) => {
    const item = workItems?.find((wi: any) => wi.bh_workitemid === id);
    return item?.bh_name || id;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Create New Item</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What needs to be done?"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <AlignLeft className="w-4 h-4 inline mr-1" />
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add more details about this task..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 resize-none"
              />
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Due Date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={startDate}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Duration
                </label>
                <div className="px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
                  {duration !== null ? `${duration} day${duration !== 1 ? 's' : ''}` : '—'}
                </div>
              </div>
            </div>

            {/* Status & Priority Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={stateId}
                    onChange={(e) => setStateId(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 appearance-none bg-white"
                  >
                    <option value="">Select status...</option>
                    {states?.map((state: any) => (
                      <option key={state.bh_stateid} value={state.bh_stateid}>
                        {state.bh_name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Flag className="w-4 h-4 inline mr-1" />
                  Priority
                </label>
                <div className="relative">
                  <select
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 appearance-none bg-white"
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Assignee & Estimate Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Assignee
                </label>
                <div className="relative">
                  <select
                    value={assigneeId}
                    onChange={(e) => setAssigneeId(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 appearance-none bg-white"
                  >
                    <option value="">Unassigned</option>
                    {members?.map((member: any) => (
                      <option key={member.bh_memberid} value={member.bh_memberid}>
                        {member.bh_name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Estimate (hours)
                </label>
                <input
                  type="number"
                  value={estimate}
                  onChange={(e) => setEstimate(e.target.value)}
                  placeholder="0"
                  min="0"
                  step="0.5"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                />
              </div>
            </div>

            {/* Parent Task */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent Task (makes this a subtask)
              </label>
              <div className="relative">
                <select
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 appearance-none bg-white"
                >
                  <option value="">No parent (top-level task)</option>
                  {workItems?.filter((wi: any) => !wi.bh_parent).map((item: any) => (
                    <option key={item.bh_workitemid} value={item.bh_workitemid}>
                      {item.bh_name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Blockers / Dependencies */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Link2 className="w-4 h-4 inline mr-1" />
                Blocked By (Dependencies)
              </label>

              {/* Selected blockers */}
              {blockedByIds.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {blockedByIds.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm"
                    >
                      <AlertCircle className="w-3 h-3" />
                      {getItemName(id)}
                      <button
                        type="button"
                        onClick={() => removeBlocker(id)}
                        className="ml-1 hover:text-red-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add blocker button/dropdown */}
              {showBlockerSelect ? (
                <div className="relative">
                  <select
                    onChange={(e) => {
                      if (e.target.value) addBlocker(e.target.value);
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 appearance-none bg-white"
                    autoFocus
                    onBlur={() => setShowBlockerSelect(false)}
                  >
                    <option value="">Select a blocking task...</option>
                    {workItems
                      ?.filter((wi: any) => !blockedByIds.includes(wi.bh_workitemid))
                      .map((item: any) => (
                        <option key={item.bh_workitemid} value={item.bh_workitemid}>
                          {item.bh_name}
                        </option>
                      ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowBlockerSelect(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg border border-dashed border-gray-300"
                >
                  <Plus className="w-4 h-4" />
                  Add blocker
                </button>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Tasks that must be completed before this one can start
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              {startDate && dueDate ? (
                <>Will appear on Gantt chart</>
              ) : (
                <span className="text-amber-600">
                  Add start & due dates to show on Gantt
                </span>
              )}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!title.trim() || !stateId || createMutation.isPending}
                className={clsx(
                  'inline-flex items-center gap-2 px-5 py-2 rounded-lg font-medium',
                  title.trim() && stateId && !createMutation.isPending
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                )}
              >
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Item'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
